/**
 * WorkflowStepCard.jsx
 *
 * Expandable card for a single workflow step.
 * Shows action name, step number badge, status indicator,
 * and renders WorkflowStepEditor when expanded.
 * Renders execution state indicators (PENDING/RUNNING/SUCCEEDED/FAILED/SKIPPED/CANCELLED).
 * Shows retry/skip buttons when a step fails.
 */

import { useState, useMemo } from 'react';
import {
    Box, Typography, Collapse, IconButton, Button, CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { getAction } from '../../../gibbs/registry';
import { StepStatus } from './constants';
import { ExecutionStatus } from '../../../gibbs/workflow';
import WorkflowStepEditor from './WorkflowStepEditor';

const statusColors = {
    [StepStatus.VALID]: '#4caf50', // green
    [StepStatus.WARNING]: '#ff9800', // yellow/orange
    [StepStatus.INVALID]: '#f44336', // red
    [StepStatus.PENDING]: '#9e9e9e', // gray
};

/** Execution state visual colors */
const executionColors = {
    [ExecutionStatus.PENDING]: '#9e9e9e', // gray
    [ExecutionStatus.RUNNING]: '#2196f3', // blue
    [ExecutionStatus.SUCCEEDED]: '#4caf50', // green
    [ExecutionStatus.FAILED]: '#f44336', // red
    [ExecutionStatus.SKIPPED]: '#9e9e9e', // gray
    [ExecutionStatus.CANCELLED]: '#9e9e9e', // gray
};

/**
 * @param {Object} props
 * @param {Object} props.action - Plan action entry { actionId, params, ... }
 * @param {number} props.stepIndex
 * @param {Object} props.params - Current overridden params for this step
 * @param {string} props.validationStatus - One of StepStatus values
 * @param {Function} props.onParamChange - (stepIndex, paramName, value) => void
 * @param {Object} [props.result] - Execution result if available
 * @param {Object} [props.executionState] - { status, error } from executor
 * @param {Function} [props.onRetry] - Called when user clicks Retry on failed step
 * @param {Function} [props.onSkip] - Called when user clicks Skip on failed step
 */
export default function WorkflowStepCard({
    action,
    stepIndex,
    params,
    validationStatus,
    onParamChange,
    result,
    executionState,
    onRetry,
    onSkip,
    disabled = false,
}) {
    const [expanded, setExpanded] = useState(false);

    const actionDef = useMemo(() => getAction(action.actionId), [action.actionId]);
    const displayName = actionDef?.name || action.actionId;

    // Determine which indicator to show: execution state takes priority over validation
    const execStatus = executionState?.status;
    const hasExecState = execStatus != null;
    const status = validationStatus || StepStatus.PENDING;
    const dotColor = hasExecState
        ? (executionColors[execStatus] || executionColors[ExecutionStatus.PENDING])
        : (statusColors[status] || statusColors[StepStatus.PENDING]);

    /**
     * Render execution state indicator icon (replaces the simple dot when executing).
     */
    const renderExecutionIndicator = () => {
        if (!hasExecState) {
            // Default validation dot
            return (
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: dotColor,
                        mr: 1,
                        flexShrink: 0,
                    }}
                />
            );
        }

        switch (execStatus) {
            case ExecutionStatus.RUNNING:
                return (
                    <CircularProgress
                        size={14}
                        thickness={4}
                        sx={{
                            mr: 1, flexShrink: 0, color: executionColors[ExecutionStatus.RUNNING],
                        }}
                    />
                );
            case ExecutionStatus.SUCCEEDED:
                return (
                    <CheckCircleIcon
                        sx={{
                            fontSize: 16,
                            mr: 1,
                            flexShrink: 0,
                            color: executionColors[ExecutionStatus.SUCCEEDED],
                        }}
                    />
                );
            case ExecutionStatus.FAILED:
                return (
                    <ErrorIcon
                        sx={{
                            fontSize: 16,
                            mr: 1,
                            flexShrink: 0,
                            color: executionColors[ExecutionStatus.FAILED],
                        }}
                    />
                );
            case ExecutionStatus.SKIPPED:
                return (
                    <RemoveCircleOutlineIcon
                        sx={{
                            fontSize: 16,
                            mr: 1,
                            flexShrink: 0,
                            color: executionColors[ExecutionStatus.SKIPPED],
                        }}
                    />
                );
            case ExecutionStatus.CANCELLED:
                return (
                    <StopCircleIcon
                        sx={{
                            fontSize: 16,
                            mr: 1,
                            flexShrink: 0,
                            color: executionColors[ExecutionStatus.CANCELLED],
                        }}
                    />
                );
            case ExecutionStatus.PENDING:
            default:
                return (
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: dotColor,
                            mr: 1,
                            flexShrink: 0,
                        }}
                    />
                );
        }
    };

    return (
        <Box
            sx={{
                mb: 1,
                border: '1px solid',
                borderColor: (() => {
                    if (execStatus === ExecutionStatus.FAILED) {
                        return executionColors[ExecutionStatus.FAILED];
                    }
                    if (execStatus === ExecutionStatus.RUNNING) {
                        return executionColors[ExecutionStatus.RUNNING];
                    }
                    return 'divider';
                })(),
                borderRadius: 1,
                overflow: 'hidden',
            }}
        >
            {/* Header row */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => setExpanded((prev) => !prev)}
            >
                {/* Step number badge */}
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        mr: 1,
                        flexShrink: 0,
                    }}
                >
                    {stepIndex + 1}
                </Box>

                {/* Execution / validation indicator */}
                {renderExecutionIndicator()}

                {/* Action name */}
                <Typography
                    variant="subtitle2"
                    sx={{
                        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                >
                    {displayName}
                </Typography>

                {/* Expand toggle */}
                <IconButton size="small" sx={{ ml: 0.5 }}>
                    {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
            </Box>

            {/* Execution status text */}
            {hasExecState && execStatus === ExecutionStatus.RUNNING && (
                <Box sx={{ px: 1.5, pb: 0.5 }}>
                    <Typography variant="caption" color="primary.main">
                        Running...
                    </Typography>
                </Box>
            )}

            {/* Failed step: error message + retry/skip buttons (Group 7) */}
            {hasExecState && execStatus === ExecutionStatus.FAILED && (
                <Box sx={{ px: 1.5, pb: 1 }}>
                    <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 0.5 }}>
                        Failed:
                        {' '}
                        {executionState.error || 'unknown error'}
                    </Typography>
                    {/*
                      * Only offer a control that is actually wired. A campaign
                      * passes no handlers: its recovery is the campaign panel's
                      * Continue/Stop, and these buttons resolved a promise that
                      * only the plain-workflow executor ever creates — so on a
                      * campaign step they rendered and did nothing at all.
                      */}
                    {(onRetry || onSkip) && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {onRetry && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={(e) => { e.stopPropagation(); onRetry(); }}
                                    sx={{
                                        textTransform: 'none', fontSize: '0.75rem', py: 0.25, minHeight: 0,
                                    }}
                                >
                                    Retry
                                </Button>
                            )}
                            {onSkip && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    onClick={(e) => { e.stopPropagation(); onSkip(); }}
                                    sx={{
                                        textTransform: 'none', fontSize: '0.75rem', py: 0.25, minHeight: 0,
                                    }}
                                >
                                    Skip
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>
            )}


            {/* Skipped indicator */}
            {hasExecState && execStatus === ExecutionStatus.SKIPPED && (
                <Box sx={{ px: 1.5, pb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                        Skipped
                    </Typography>
                </Box>
            )}

            {/* Cancelled indicator */}
            {hasExecState && execStatus === ExecutionStatus.CANCELLED && (
                <Box sx={{ px: 1.5, pb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                        Cancelled
                    </Typography>
                </Box>
            )}

            {/* Succeeded indicator */}
            {hasExecState && execStatus === ExecutionStatus.SUCCEEDED && (
                <Box sx={{ px: 1.5, pb: 0.5 }}>
                    <Typography variant="caption" color="success.main">
                        Completed
                    </Typography>
                </Box>
            )}

            {/* Legacy result display (for single_action mode) */}
            {!hasExecState && result && (
                <Box sx={{ px: 1.5, pb: 0.5 }}>
                    <Typography variant="caption" color={result.success ? 'success.main' : 'error.main'}>
                        {result.success ? 'Completed' : `Failed: ${result.error || 'unknown error'}`}
                    </Typography>
                </Box>
            )}

            {/* Expanded editor */}
            <Collapse in={expanded}>
                <Box sx={{ px: 1.5, pb: 1.5 }}>
                    {actionDef ? (
                        <WorkflowStepEditor
                            actionDef={actionDef}
                            params={params}
                            onParamChange={onParamChange}
                            stepIndex={stepIndex}
                            disabled={disabled}
                        />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Unknown action:
                            {' '}
                            {action.actionId}
                        </Typography>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
}
